import type { InputMaterialWithQuantity, Operation } from "../Machine";
import { ConsumableAmount } from "../Consumable";
import type { ToolId } from "../Tool";
import {
  AssembledPart,
  Board,
  BoardDimension,
  boardEnds,
  FinishedProduct,
  FinishedProductType,
  MaterialInstance,
  Panel,
  panelWidth,
  SheetGood,
  Species,
} from "../Materials";
import { makeMaterial, materialMeetsInput } from "../material-helpers";
import { isPanel } from "../panel-helpers";
import { isProductPlanId, PLAN_IDS, PlanId } from "../plans";
import { fastenerJoins } from "./plan-geometry";
import {
  FastenerId,
  FastenerRecord,
  orientEnds,
  PLAN_RECORDS,
  PlanRecord,
  SlotRecord,
  stockRequirement,
} from "./plan-record";

/**
 * Product blueprints: the single authored artifact behind an assembled
 * product. Pallet dismantling proved the pattern — a product visibly
 * made of its parts, one nail per crossing, one sprite composing real
 * board sprites at every zoom — and assembly is that machinery run
 * forward: a blueprint says where every part lies and where every
 * fastener goes, the player lays real staged parts onto ghost slots and
 * drives one fastener per crossing. Every assembly in the game is a
 * blueprint build (`interaction.blueprint` is required), products and
 * shop equipment alike — an equipment blueprint's commit grants the
 * machine/upgrade/jig instead of leaving a product.
 *
 * One blueprint is four things that previously could drift apart:
 * the recipe's input list (blueprintInputs), its fastener cost
 * (blueprintFastenerCost), the finished product's rendering
 * (AssembledProductSprite draws the slots' actual parts, grain and all),
 * and the bench view's assembly script (ghost outlines at the slots,
 * nails driven at the fasteners).
 *
 * Blueprints are authored as plan records — the JSON in
 * `src/game/plans/`, written by the blueprint editor and described in
 * `plan-record.ts` — and this module is the runtime reading of one: the
 * record's slots with their derived stock requirements, its fasteners
 * as stored. The settled rules:
 *
 *  - Fasteners are stored, never derived at load: a record carries
 *    every one — where its head sits, and whether it is driven down
 *    from above or level from the side. What each one joins is read
 *    off the boxes when the blueprint is built: the first two parts
 *    its shank passes through (`fastenerJoins` in plan-geometry.ts),
 *    the one the head is in first. `deriveFasteners` is the editor's
 *    auto-place — one down through every lap, a pair level into every
 *    backed board end — and the author can then move, add, or delete
 *    individually.
 *  - Products carry their bill of materials: FinishedProduct.parts
 *    records the very boards that went in — the grain the player laid
 *    on the bench is the grain in the finished piece everywhere it
 *    draws. Older saves render the blueprint's nominal stock
 *    (defaultPartsFor). Blueprint-assembled products never get flat
 *    art.
 *  - Seating is derived, not stored: a part is "seated" when its
 *    persistent bench placement lies on its slot, so a refresh
 *    mid-assembly finds every part as placed. Driven fasteners are
 *    stored (`MachineState.assembly`): each one is spent out of the
 *    supplies as it goes in (`sim/commands/assembly-commands.ts`), so
 *    the record has to outlive the view; the last fastener resolves
 *    start + finish back to back. A part a driven fastener holds won't
 *    drag, turn, or leave the bench until the build commits or the
 *    fastener is pulled back out.
 *  - The ghost frame is the product's default seat: the assembled
 *    sprite appears exactly where the parts were lying — nothing moves
 *    at the moment the boards become one piece.
 *  - A fastener-less blueprint (the material shelf) commits when its
 *    last part is laid on.
 *  - Slots are matched in declaration order (matchPartsToSlots), so a
 *    plan lists its most constrained slots first — a mitered front
 *    before a plain floor that would otherwise take the mitered board.
 */

/** One part's place in the finished product: the record's slot plus
 * the matcher its stock declaration comes to. */
export interface BlueprintSlot extends SlotRecord {
  /** What stock fills the slot — the same matcher recipes already use.
   * Usually a board; a jig's base or a worktable's top is a sheet good
   * (typed as board stock for the consumers that read board fields —
   * matching goes through materialMeetsInput either way). */
  readonly requirement: InputMaterialWithQuantity<Board>;
}

/** A fastener as the bench arms it: the record's head and direction,
 * plus the two parts its shank passes through, derived from the boxes. */
export interface BlueprintFastener extends FastenerRecord {
  /** [the part the head is in, the part the point ends in]. */
  readonly joins: readonly [string, string];
}

export type EquipmentBlueprintId = Exclude<PlanId, FinishedProductType>;
export type BlueprintId = PlanId;

export interface ProductBlueprint {
  /** Registry key; for product blueprints it equals the product type. */
  readonly id: BlueprintId;
  /** The material the build becomes — absent for shop equipment, whose
   * commit grants machines or upgrades instead. */
  readonly productType?: FinishedProductType;
  /** Product footprint, in inches (drawn lying flat, like on the bench). */
  readonly widthIn: number;
  readonly heightIn: number;
  readonly slots: ReadonlyArray<BlueprintSlot>;
  readonly fasteners: ReadonlyArray<BlueprintFastener>;
  readonly fastenerConsumable: FastenerId;
}

// ---------------------------------------------------------------------
// From record to blueprint

/** The runtime blueprint a plan record describes. Pure: the editor
 * builds one from its unsaved record for the live preview. Throws for a
 * fastener whose shank passes through fewer than two parts — a nail
 * into nothing is a plan that can't be built, and the editor's lint
 * names it before it gets this far. */
export function blueprintFromRecord(record: PlanRecord): ProductBlueprint {
  return {
    id: record.id,
    ...(record.builds === "product"
      ? { productType: record.id as FinishedProductType }
      : {}),
    widthIn: record.widthIn,
    heightIn: record.heightIn,
    slots: record.slots.map((slot) => ({
      ...slot,
      requirement: stockRequirement(slot),
    })),
    fasteners: record.fasteners.map((fastener, index) => {
      const joins = fastenerJoins(record.slots, fastener);
      if (!joins) {
        throw new Error(
          `${record.id}: fastener ${index + 1} at (${fastener.xIn}, ${fastener.yIn}) passes through fewer than two parts`,
        );
      }
      return { ...fastener, joins };
    }),
    fastenerConsumable: record.fastener,
  };
}

const BLUEPRINTS: Readonly<Record<BlueprintId, ProductBlueprint>> = (() => {
  const blueprints = {} as Record<BlueprintId, ProductBlueprint>;
  for (const id of PLAN_IDS) {
    const record = PLAN_RECORDS[id];
    if ((record.builds === "product") !== isProductPlanId(id)) {
      throw new Error(
        `${id}.json builds ${record.builds} but is filed under ${
          isProductPlanId(id) ? "products" : "equipment"
        } in plans/index.ts`,
      );
    }
    blueprints[id] = blueprintFromRecord(record);
  }
  return blueprints;
})();

/** The blueprint behind a plan id. */
export function blueprintFor(id: BlueprintId): ProductBlueprint {
  return BLUEPRINTS[id];
}

/** The blueprint behind an assembled product type, or null — machine-
 * made products (the cutting boards) have none. */
export function productBlueprintFor(
  type: string | undefined,
): ProductBlueprint | null {
  return (type && BLUEPRINTS[type as BlueprintId]) || null;
}

// ---------------------------------------------------------------------
// The recipe, read off the blueprint

/**
 * The recipe's input list, derived from the slots so the plan and the
 * bench can never disagree: slots with identical stock fold into one
 * row with a quantity, rows in order of first appearance.
 */
export function blueprintInputs(
  blueprint: ProductBlueprint,
): ReadonlyArray<InputMaterialWithQuantity<Board>> {
  const rows = new Map<string, InputMaterialWithQuantity<Board>>();
  for (const slot of blueprint.slots) {
    // The requirement is the identity, however the parts are turned.
    // Stringifying drops the ends predicate, so the nominal ends that
    // built it stand in for it
    const key = JSON.stringify({
      requirement: slot.requirement,
      ends: slot.part.ends,
    });
    const row = rows.get(key);
    if (row) {
      rows.set(key, { ...row, quantity: row.quantity + 1 });
    } else {
      rows.set(key, { ...slot.requirement, quantity: 1 });
    }
  }
  return [...rows.values()];
}

/** The tool that drives a blueprint's fasteners in the bench view:
 * nails take the hammer, screws the drill. */
export function fastenerToolId(consumable: FastenerId): ToolId {
  return consumable === "screws" ? "drill" : "hammer";
}

/** The fastener bill: one of the plan's fastener per fastener. */
export function blueprintFastenerCost(
  blueprint: ProductBlueprint,
): ReadonlyArray<ConsumableAmount> {
  return blueprint.fasteners.length > 0
    ? [{ id: blueprint.fastenerConsumable, amount: blueprint.fasteners.length }]
    : [];
}

/**
 * Everything an operation draws from the supply cabinet over its whole
 * run: a build's fastener bill (spent one fastener at a time as they're
 * driven, so never on the operation as `requiredConsumables`), or the
 * supplies any other recipe bills at its start. What the drawer, the
 * station sheet, and the floor's refusals read against the stock.
 */
export function operationSupplies(
  operation: Operation,
): ReadonlyArray<ConsumableAmount> {
  const interaction = operation.interaction;
  const blueprint =
    interaction?.kind === "assembly"
      ? productBlueprintFor(interaction.blueprint)
      : null;
  return blueprint
    ? blueprintFastenerCost(blueprint)
    : (operation.requiredConsumables ?? []);
}

/**
 * Which staged material fills which slot, in slot order — the same
 * greedy matching the operation's claim uses, so the bill of materials
 * lines up with what was actually taken.
 */
export function matchPartsToSlots(
  blueprint: ProductBlueprint,
  materials: ReadonlyArray<MaterialInstance>,
): ReadonlyArray<{
  slot: BlueprintSlot;
  material: Board | Panel | SheetGood;
}> {
  const pool = [...materials];
  return blueprint.slots.map((slot) => {
    const index = pool.findIndex((material) =>
      materialMeetsInput(material, slot.requirement),
    );
    if (index === -1) {
      throw new Error(
        `No staged piece fits the ${slot.role} slot of ${blueprint.id}`,
      );
    }
    const material = pool[index] as Board | Panel | SheetGood;
    pool.splice(index, 1);
    return { slot, material };
  });
}

/** The species most of the parts share — the product's face color. */
function dominantSpecies(parts: ReadonlyArray<{ species: Species }>): Species {
  const counts = new Map<Species, number>();
  for (const part of parts) {
    counts.set(part.species, (counts.get(part.species) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

/**
 * The finished product, carrying its bill of materials: the very boards
 * that went in, seeded by their ids so each part keeps the grain it had
 * lying on the bench — assembly fastens the boards, it doesn't swap
 * them for different ones. A board's ends are recorded the way the slot
 * needs them (orientEnds): the same board, laid the way the corners
 * close. Ends that match no way are kept as recorded.
 */
export function assembleFromBlueprint(
  blueprint: ProductBlueprint,
  materials: ReadonlyArray<MaterialInstance>,
): FinishedProduct {
  const productType = blueprint.productType;
  if (!productType) {
    throw new Error(
      `The ${blueprint.id} blueprint builds equipment, not a product`,
    );
  }
  const matched = matchPartsToSlots(blueprint, materials);
  const parts: AssembledPart[] = matched.map(({ slot, material }) => {
    // A glued-up part keeps its strips — the tray's bottom shows the
    // very stripes the player glued
    if (isPanel(material)) {
      return {
        slot: slot.id,
        species: dominantSpecies(material.strips),
        width: panelWidth(material),
        length: material.length,
        thickness: material.thickness,
        surface: material.surface,
        strips: material.strips,
        seed: material.id,
      };
    }
    // A sheet part keeps its kind; it has no species of its own, so it
    // stands as pine where one is asked for
    if (material.type === "plywood") {
      return {
        slot: slot.id,
        species: "pine",
        width: slot.part.widthIn,
        length: slot.part.lengthIn,
        thickness: material.thickness,
        sheetKind: material.kind,
        seed: material.id,
      };
    }
    const ends =
      material.ends && slot.part.ends
        ? (orientEnds(boardEnds(material), slot.part.ends) ?? material.ends)
        : material.ends;
    return {
      slot: slot.id,
      species: material.species,
      width: material.width,
      length: material.length,
      thickness: material.thickness,
      // A sanded board stays sanded in the finished piece — the bookshelf
      // is built from surfaced stock, not pallet wood
      surface: material.surface,
      // …and a mitered board stays mitered: a frame rail's corners are
      // its ends, visible in the finished piece
      ...(ends ? { ends } : {}),
      seed: material.id,
    };
  });
  // A build with a panel part reads its species off that face — the
  // tray's bottom, the table's top — not off a headcount its four legs
  // would win
  const facePart = parts.find((part) => part.strips);
  const woodParts = parts.filter((part) => !part.sheetKind);
  return makeMaterial<FinishedProduct>({
    type: productType,
    species: facePart
      ? facePart.species
      : dominantSpecies(woodParts.length > 0 ? woodParts : parts),
    parts,
  });
}

/**
 * Stand-in parts for a product saved before products carried their
 * bill of materials: the blueprint's nominal stock in the product's own
 * species, seeded off the product id so the grain is stable.
 */
export function defaultPartsFor(
  blueprint: ProductBlueprint,
  product: FinishedProduct,
): ReadonlyArray<AssembledPart> {
  return blueprint.slots.map((slot) => ({
    slot: slot.id,
    species: product.species,
    width: slot.part.widthIn as BoardDimension,
    length: slot.part.lengthIn,
    thickness: slot.part.thicknessQ as BoardDimension,
    ...(slot.stock.surface ? { surface: slot.stock.surface } : {}),
    // The slot's nominal ends, so a frame from before parts carried end
    // treatments still draws closed corners
    ...(slot.part.ends ? { ends: slot.part.ends } : {}),
    seed: `${product.id}:${slot.id}`,
  }));
}
